import React, { useMemo, useState, useEffect } from 'react';
import { Package, Leaf, Truck, ClipboardList, X, Plus, AlertTriangle, SlidersHorizontal, ShoppingCart, Copy, Check, ChevronDown, TrendingUp, BarChart3 } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Area, XAxis, YAxis, Tooltip, ReferenceDot } from 'recharts';
import PageShell from '../components/PageShell';
import { SectionCard, Loader, ErrorBox, INPUT, CALUM } from '../components/charts';
import { useApi } from '../hooks/useApi';
import { useT } from '../lib/i18n';
import api from '../lib/api';
import { formatNumber, formatDateLocale } from '../lib/format';

/*
 * Voorraadbeheer — two inventory layers on one model:
 *   packaging  : disposables (cups, lids…) consumed by take-away sales only
 *   ingredient : coffee, milk, … consumed by every sale via a recipe per product
 * Stock = last physical count + deliveries − usage derived from sales.
 */
const LAYERS = [['packaging', 'Verpakking', Package], ['ingredient', 'Ingrediënten', Leaf]];
const UNITS = { packaging: ['stuks'], ingredient: ['g', 'kg', 'ml', 'l', 'stuks'] };
const KIND_LABEL = { count: 'Telling', delivery: 'Levering', adjust: 'Correctie' };
const KIND_ICON = { count: ClipboardList, delivery: Truck, adjust: SlidersHorizontal };
// Today's date in the browser's local zone (toISOString alone would give UTC).
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const layerOf = (i) => i.layer || 'packaging';

export default function Packaging() {
  const t = useT();
  const { data, loading, error, reload } = useApi(() => api.packaging(), []);
  const [layer, setLayer] = useState('packaging');
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [quick, setQuick] = useState(null); // {packaging_id, kind} from a card button

  if (loading) return <PageShell page="Voorraadbeheer" accent="green"><Loader /></PageShell>;
  if (error) return <PageShell page="Voorraadbeheer" accent="green"><ErrorBox error={error} onRetry={reload} /></PageShell>;
  if (!data) return null;

  const layerItems = data.items.filter((i) => layerOf(i) === layer);
  const items = layerItems.filter((i) => showInactive || i.active);
  const active = layerItems.filter((i) => i.active);
  const activeIds = new Set(active.map((i) => i.id));
  const kinds = [...new Set(layerItems.map((i) => i.kind).filter(Boolean))];
  // the order list and the to-do panel follow the layer tab you are looking at
  const layerIds = new Set(layerItems.map((i) => i.id));
  const layerTodo = Object.fromEntries(Object.entries(data.todo || {})
    .map(([k, v]) => [k, v.filter((x) => layerIds.has(x.id))]));
  const fmtDate = formatDateLocale;
  // an item nobody uses (no products link to it) can be switched off from the
  // to-do list itself; the history is kept either way
  const deactivate = async (x) => {
    const it = data.items.find((i) => i.id === x.id);
    if (!it || !window.confirm(t('Dit item niet meer opvolgen?'))) return;
    await api.setPackagingItem({ id: it.id, name: it.name, kind: it.kind, active: false });
    await reload();
  };
  const onQuick = (packaging_id, kind) => {
    setQuick({ packaging_id: String(packaging_id), kind, at: Date.now() });
    document.getElementById('stock-update')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PageShell page="Voorraadbeheer" accent="green"
      freshness={data.last_data_date ? `${t('Verkoopdata tot')} ${fmtDate(data.last_data_date)}` : null}>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>{t('Voorraadbeheer')}</h1>
          <p className="text-sm text-slate-500">{t('Voorraad en verbruiksprognose — verpakking uit de take-away-verkopen, ingrediënten uit alle verkopen.')}</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-500">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t('Toon inactieve items')}
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5 text-xs font-medium">
          {LAYERS.map(([k, label, Icon]) => {
            const n = data.items.filter((i) => layerOf(i) === k && i.active).length;
            return (
              <button key={k} onClick={() => { setLayer(k); setAdding(false); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md ${layer === k ? 'bg-white text-[#114817] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Icon className="h-3.5 w-3.5" /> {t(label)} <span className="text-[10px] text-slate-400">{n}</span>
              </button>
            );
          })}
        </div>
        <button onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1 rounded-lg bg-[#114817] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#186020]">
          <Plus className="h-3.5 w-3.5" /> {layer === 'ingredient' ? t('Ingrediënt toevoegen') : t('Verpakking toevoegen')}
        </button>
        <span className="text-[10px] text-slate-400 ml-auto hidden sm:inline">
          <span className="inline-block h-2 w-2 rounded-full align-middle" style={{ background: CALUM.gold }} /> {t('levering')} ·{' '}
          <span className="inline-block h-2 w-2 rounded-full align-middle" style={{ background: CALUM.green }} /> {t('telling')}
        </span>
      </div>

      {adding && <AddItem layer={layer} kinds={kinds} t={t} onDone={async () => { setAdding(false); await reload(); }} onCancel={() => setAdding(false)} />}

      <OrderList rows={(data.order_list || []).filter((r) => layerIds.has(r.id))} t={t} fmtDate={fmtDate}
        unitOf={(r) => (r.unit && r.unit !== 'stuks' ? r.unit : '')} />
      <Todo todo={layerTodo} t={t} fmtDate={fmtDate} onQuick={onQuick} onDeactivate={deactivate} />

      {layer === 'packaging' && !data.takeaway_only && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{t('Take Away-info ontbreekt nog in de verkoopdata — de prognose telt voorlopig álle verkopen. Upload een recente transacties-export op de Data-pagina, daarna telt enkel take-away.')}</span>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm px-3 sm:px-4 py-1 mb-6">
        <div className="hidden sm:flex items-center gap-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <div className="w-44 lg:w-56">{t('Item')}</div>
          <div className="w-40">{t('Verloop (42 d.)')}</div>
          <div className="w-24 text-right">{t('Voorraad')}</div>
          <div className="w-28">{t('Verbruik')}</div>
          <div className="hidden lg:block flex-1">{t('Laatste telling')}</div>
        </div>
        {sortItems(items).map((it, idx) => (
          <ItemRow key={it.id} it={it} t={t} fmtDate={fmtDate} onQuick={onQuick}
            defaultOpen={idx === 0 && it.usage?.per_week > 0} />
        ))}
        {!items.length && <p className="text-sm text-slate-400 py-3">{layer === 'ingredient' ? t('Nog geen ingrediënten — voeg er een toe en leg per product het recept vast.') : t('Nog geen verpakkingsitems.')}</p>}
      </div>

      <div className="mb-6" id="stock-update">
        <StockUpdate items={active} events={data.events.filter((e) => activeIds.has(e.packaging_id))}
          quick={quick} t={t} fmtDate={fmtDate} reload={reload} />
      </div>

      <div className="mb-6">
        {layer === 'packaging'
          ? <MappingSection items={active} products={data.products} t={t} reload={reload} />
          : <RecipeSection items={active} products={data.products} t={t} reload={reload} />}
      </div>

      <ManageItems items={layerItems} t={t} reload={reload} />
    </PageShell>
  );
}

/* --------------------------------------------------------------- order list */
const ORDER_TONE = {
  late: 'border-red-200 bg-red-50 text-red-800',
  now: 'border-amber-300 bg-amber-50 text-amber-900',
  soon: 'border-slate-200 bg-white text-slate-700',
};

/**
 * What to order and by when. The forecast says how fast an item is used and
 * when it runs out; the lead time says how long a delivery takes. Ordering
 * later than "runs out − lead time" means standing there empty, so that date,
 * not the run-out date, is what the café needs to see. Quantity covers the
 * lead time plus the cover you want to hold, rounded up to whole packs.
 */
function OrderList({ rows, t, fmtDate, unitOf }) {
  const [copied, setCopied] = useState(null);
  if (!rows.length) return null;

  const bySupplier = rows.reduce((m, r) => {
    const k = r.supplier || '';
    (m[k] = m[k] || []).push(r);
    return m;
  }, {});

  const copy = async (supplier, list) => {
    const text = list.map((r) => `${r.packs} × ${r.name}`
      + (r.pack_size > 1 ? ` (${formatNumber(r.pack_size)} ${unitOf(r) || t('stuks')}/doos)` : '')).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(supplier);
      setTimeout(() => setCopied(null), 2500);
    } catch { /* clipboard blocked — the list stays readable on screen */ }
  };

  return (
    <SectionCard className="mb-6" title={<span className="inline-flex items-center gap-1.5"><ShoppingCart className="h-4 w-4 text-[#114817]" /> {t('Te bestellen')}</span>}
      subtitle={t('Hoeveel en tegen wanneer, zodat de levering er is vóór de voorraad op is.')}>
      {Object.entries(bySupplier).map(([supplier, list]) => (
        <div key={supplier} className="mb-3 last:mb-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {supplier || t('geen leverancier ingesteld')}
            </div>
            <button type="button" onClick={() => copy(supplier, list)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-500 hover:text-slate-700">
              {copied === supplier ? <><Check className="h-3 w-3 text-emerald-600" /> {t('Gekopieerd')}</> : <><Copy className="h-3 w-3" /> {t('Kopieer lijst')}</>}
            </button>
          </div>
          <div className="space-y-1.5">
            {list.map((r) => (
              <div key={r.id} className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm ${ORDER_TONE[r.status] || ORDER_TONE.soon}`}>
                <span className="font-semibold tabular-nums">
                  {r.pack_size > 1 ? `${formatNumber(r.packs)}×` : formatNumber(r.need)}
                </span>
                <span className="font-medium min-w-0 flex-1 truncate">{r.name}</span>
                <span className="text-[11px] opacity-80 tabular-nums">
                  {r.pack_size > 1
                    ? <>{formatNumber(r.pack_size)} {t('per doos')} · {formatNumber(r.need)} {unitOf(r) || t('stuks')} {t('nodig')}</>
                    : <>{unitOf(r) || t('stuks')} · {t('stel een doosgrootte in bij Items beheren')}</>}
                </span>
                <span className="text-[11px] font-semibold tabular-nums ml-auto">
                  {r.status === 'late' ? t('nu bestellen — voorraad is op')
                    : r.order_by ? `${t('bestel vóór')} ${fmtDate(r.order_by)}` : t('nu bestellen')}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </SectionCard>
  );
}

/**
 * What still has to be filled in before an item can produce advice.
 *
 * Only items are listed, never products: a product without a cup simply does
 * not consume one (a croissant, an oat-milk extra) and is no gap. An item with
 * no products linked is a different matter — it can never move — so that one
 * offers to be switched off rather than nagging for a link that may not exist.
 */
function Todo({ todo, t, fmtDate, onQuick, onDeactivate }) {
  const groups = [
    ['needs_count', t('Nog nooit geteld — zonder telling is er geen voorraad en dus geen advies'), 'count'],
    ['needs_links', t('Geen producten gekoppeld — koppel producten of zet het item op inactief'), null],
    ['needs_recount', t('Voorraad staat negatief — de telling klopt niet meer of een levering is niet ingeboekt; tel opnieuw'), 'count'],
    ['stale_counts', t('Telling ouder dan zes weken — de schatting loopt weg van de werkelijkheid'), 'count'],
  ].filter(([k]) => (todo[k] || []).length);
  if (!groups.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 px-3 sm:px-4 py-3">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-900 mb-2">
        <AlertTriangle className="h-4 w-4" /> {t('Nog aan te vullen')}
      </div>
      {groups.map(([key, label, action]) => (
        <div key={key} className="mb-2 last:mb-0">
          <div className="text-[11px] text-amber-800 mb-1">{label}</div>
          <div className="flex flex-wrap gap-1.5">
            {todo[key].map((x) => (
              <span key={x.id} className="inline-flex items-center rounded-md border border-amber-300 bg-white text-[11px] text-amber-900 overflow-hidden">
                <button type="button" disabled={!action} onClick={() => action && onQuick(x.id, action)}
                  className={`px-2 py-1 ${action ? 'hover:bg-amber-50' : 'cursor-default'}`}>
                  {x.name}
                  {x.est_stock != null ? ` · ${formatNumber(x.est_stock)}` : x.date ? ` · ${fmtDate(x.date)}` : ''}
                </button>
                {key === 'needs_links' && (
                  <button type="button" onClick={() => onDeactivate(x)} title={t('Zet op inactief')}
                    className="border-l border-amber-200 px-1.5 py-1 text-amber-500 hover:bg-amber-50 hover:text-amber-800">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- add item */
function AddItem({ layer, kinds, t, onDone, onCancel }) {
  const units = UNITS[layer];
  const [form, setForm] = useState({ name: '', kind: '', unit: units[0] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    if (!form.name.trim()) return;
    setBusy(true); setErr(null);
    try { await api.setPackagingItem({ ...form, layer }); await onDone(); }
    catch (e) { setErr(e?.response?.data?.detail || t('Opslaan mislukt.')); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-4 rounded-xl border border-[#8bd4be] bg-[#8bd4be]/10 px-3 py-3">
      <div className="text-xs font-semibold text-[#114817] mb-2">{layer === 'ingredient' ? t('Nieuw ingrediënt') : t('Nieuwe verpakking')}</div>
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-end gap-2">
        <input autoFocus placeholder={layer === 'ingredient' ? t('Naam (bv. Koffiebonen, Havermelk)') : t('Naam (bv. Cup Espresso)')} value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && save()}
          className={`${INPUT} col-span-2 sm:flex-1 sm:min-w-[200px]`} />
        <input placeholder={t('Groep (bv. boissons)')} value={form.kind} list="inv-kinds"
          onChange={(e) => setForm({ ...form, kind: e.target.value })} className={`${INPUT} sm:w-36`} />
        <datalist id="inv-kinds">{kinds.map((k) => <option key={k} value={k} />)}</datalist>
        <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={`${INPUT} sm:w-24`} title={t('Eenheid')}>
          {units.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <button onClick={save} disabled={busy || !form.name.trim()}
          className="rounded-lg bg-[#114817] text-white px-4 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold disabled:opacity-40">{t('Opslaan')}</button>
        <button onClick={onCancel} className="text-xs text-slate-500 underline px-2">{t('Annuleren')}</button>
      </div>
      {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
    </div>
  );
}

/* --------------------------------------------------------------- item card */
const SparkTip = ({ active, payload, t, unit }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-2.5 py-2 text-[11px] leading-relaxed">
      <div className="font-semibold text-slate-700">{formatDateLocale(r.date)}</div>
      <div className="text-slate-600">{t('voorraad')} <b className="tabular-nums">{r.stock != null ? formatNumber(r.stock) : '—'}</b>{unit ? ` ${unit}` : ''}</div>
      {r.usage > 0 && <div className="text-slate-500">{t('verbruik')} −{formatNumber(r.usage)}</div>}
      {r.delivery != null && <div style={{ color: '#7a5a1e' }}>{t('levering')} {r.delivery > 0 ? '+' : ''}{formatNumber(r.delivery)}</div>}
      {r.count != null && <div style={{ color: CALUM.green }}>{t('telling')} {formatNumber(r.count)}</div>}
    </div>
  );
};

function Sparkline({ history, t, unit }) {
  const rows = history || [];
  if (!rows.some((r) => r.stock != null)) {
    return <div className="h-8 rounded-md bg-slate-50 text-[10px] text-slate-400 flex items-center justify-center">{t('geen telling — nog geen verloop')}</div>;
  }
  const dots = rows.filter((r) => r.stock != null && (r.delivery != null || r.count != null));
  return (
    <div className="h-8">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 4, right: 5, bottom: 1, left: 5 }}>
          <XAxis dataKey="date" hide />
          <YAxis hide domain={[0, 'auto']} />
          <Tooltip content={<SparkTip t={t} unit={unit} />} cursor={{ stroke: '#cbd5e1' }} />
          <Area type="monotone" dataKey="stock" stroke={CALUM.green} fill={CALUM.green} fillOpacity={0.12}
            strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
          {dots.map((r) => (
            <ReferenceDot key={r.date} x={r.date} y={r.stock} r={3}
              fill={r.count != null ? CALUM.green : CALUM.gold} stroke="#fff" strokeWidth={1} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ------------------------------------------------------- usage per item */
const WD_SHORT = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const WD_FULL = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];

/** A row of bars scaled to the biggest value — enough to read a rhythm at a
 *  glance. The bar area has a fixed height and the labels flow underneath, so
 *  nothing can overrun the heading above it. */
function Bars({ rows, highlight }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="flex items-end gap-1">
      {rows.map((r, i) => (
        <div key={i} className="flex-1 min-w-0 flex flex-col items-center gap-0.5" title={r.title || ''}>
          <span className="text-[10px] tabular-nums text-slate-500 leading-none h-3">
            {r.value ? formatNumber(Math.round(r.value)) : ''}
          </span>
          <div className="w-full h-16 flex items-end">
            <div className="w-full rounded-t"
              style={{ height: `${Math.max(3, (r.value / max) * 100)}%`,
                background: r.label === highlight ? CALUM.green : r.tone || CALUM.gold,
                opacity: r.value ? 1 : 0.25 }} />
          </div>
          <span className={`text-[10px] truncate w-full text-center leading-none ${r.label === highlight ? 'font-semibold text-[#114817]' : 'text-slate-400'}`}>
            {r.label}
          </span>
          {r.sub != null && (
            <span className={`text-[9px] tabular-nums leading-none ${r.sub >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {r.sub >= 0 ? '+' : ''}{r.sub}%
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The picture behind one item: how much goes out on a normal day and a normal
 * week (that is the number you order on), which weekday carries it, how the
 * last weeks moved, and the days that broke the pattern.
 */
function UsagePanel({ it, t, fmtDate }) {
  const u = it.usage || {};
  const unit = it.unit && it.unit !== 'stuks' ? it.unit : t('stuks');
  const weeks = (it.weekly_usage || []).slice(-8);
  // a weekday the café opens only now and then (an event Sunday) is not part of
  // the weekly rhythm — it is named underneath instead of towering over it
  const rare = (it.weekday_profile || []).filter((w) => w.open_frac < 0.25 && w.avg > 0);
  const big = (v, label, sub) => (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 min-w-[7rem]">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-xl font-bold text-slate-900 tabular-nums leading-tight" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>
        {formatNumber(v)} <span className="text-[11px] font-normal text-slate-400">{unit}</span>
      </div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
  return (
    <div className="bg-slate-50/70 border-t border-slate-100 px-3 sm:px-4 py-3 -mx-3 sm:-mx-4">
      <div className="flex flex-wrap gap-2 mb-3">
        {big(u.per_week ?? 0, t('per week'), t('gemiddeld over 8 weken'))}
        {big(u.per_open_day ?? 0, t('per open dag'), `${formatNumber(u.open_days || 0)} ${t('open dagen geteld')}`)}
        {it.order?.pack_size > 1 && big(
          Math.round(((u.per_week || 0) / it.order.pack_size) * 10) / 10, t('dozen per week'),
          `${formatNumber(it.order.pack_size)} ${t('per doos')}`)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="text-[11px] font-semibold text-slate-500 mb-2">{t('Op een normale dag')}</div>
          <Bars highlight={u.busiest_weekday ? WD_SHORT[WD_FULL.indexOf(u.busiest_weekday)] : null}
            rows={(it.weekday_profile || []).map((w, i) => [w, i])
              .filter(([w]) => w.open_frac >= 0.25)
              .map(([w, i]) => ({
                label: WD_SHORT[i], value: w.avg,
                title: `${w.weekday}: ${formatNumber(Math.round(w.avg))} ${unit} ${t('per open dag')} (${w.open_days}×)`,
              }))} />
          {rare.length > 0 && (
            <p className="text-[10px] text-slate-400 mt-1">
              {rare.map((w) => `${w.weekday.toLowerCase()}: ${formatNumber(Math.round(w.avg))} ${unit} · ${w.open_days}× ${t('open in 8 weken')}`).join(' · ')}
            </p>
          )}
        </div>
        <div>
          <div className="text-[11px] font-semibold text-slate-500 mb-2">{t('Per week — afwijking t.o.v. een normale week')}</div>
          <Bars rows={weeks.map((w) => ({
            label: `W${w.week.split('-W')[1]}`, value: w.qty, sub: w.vs_avg_pct,
            tone: Math.abs(w.vs_avg_pct ?? 0) >= 25 ? CALUM.clay : CALUM.gold,
            title: `${w.week}: ${formatNumber(w.qty)} ${unit}`,
          }))} />
        </div>
      </div>

      <div className="mt-3">
        <div className="text-[11px] font-semibold text-slate-500 mb-1 inline-flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> {t('Dagen die afweken')}
        </div>
        {u.spikes?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {u.spikes.map((sp) => (
              <span key={sp.date}
                className={`rounded-md border px-2 py-1 text-[11px] tabular-nums ${sp.pct >= 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
                {fmtDate(sp.date)} · {formatNumber(sp.qty)} {t('i.p.v.')} {formatNumber(sp.expected)}{' '}
                <b>{sp.pct >= 0 ? '+' : ''}{sp.pct}%</b>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-slate-400">{t('Geen uitschieters — het verbruik volgt het gewone weekpatroon.')}</p>
        )}
      </div>
    </div>
  );
}

// what runs out first on top; items without a forecast keep their name order
const sortItems = (items) => [...items].sort((a, b) =>
  (a.stockout_date || '9999') < (b.stockout_date || '9999') ? -1
    : (a.stockout_date || '9999') > (b.stockout_date || '9999') ? 1 : a.name.localeCompare(b.name));

function ItemRow({ it, t, fmtDate, onQuick, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const soon = it.stockout_date && (new Date(it.stockout_date) - new Date()) / 86400000 < 14;
  const unit = it.unit && it.unit !== 'stuks' ? it.unit : '';
  return (
    <div className="border-b border-slate-100 last:border-0">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      {/* name — first line on mobile, first column on desktop */}
      <div className="min-w-0 flex-1 sm:flex-none sm:w-44 lg:w-56">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="flex items-center gap-1 text-sm font-medium text-slate-900 hover:text-[#114817] max-w-full">
          <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          <span className="truncate" title={it.name}>{it.name}</span>
        </button>
        <div className="text-[10px] text-slate-400 truncate">
          {it.kind || (layerOf(it) === 'ingredient' ? t('ingrediënt') : t('verpakking'))}
          {!it.active && ` · ${t('inactief')}`}
          {it.products.length > 0 && ` · ${it.products.length} ${t('producten')}`}
        </div>
      </div>
      {/* stock */}
      <div className="text-right shrink-0 w-24 sm:order-3">
        <div className={`text-base font-bold tabular-nums leading-tight ${soon ? 'text-red-600' : 'text-slate-900'}`}>
          {it.est_stock !== null ? formatNumber(it.est_stock) : '—'}{unit && <span className="ml-0.5 text-[10px] font-normal text-slate-400">{unit}</span>}
        </div>
        <div className={`text-[10px] ${soon ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
          {it.stockout_date ? `${t('op rond')} ${fmtDate(it.stockout_date)}` : it.est_stock !== null ? t('geschatte voorraad') : t('geen telling')}
        </div>
        {it.est_stock < 0 ? (
          // the quantity would be derived from an impossible stock — ask for a
          // recount instead of naming a number nobody should order on
          <div className="text-[10px] font-semibold text-amber-700">{t('tel opnieuw')}</div>
        ) : it.order && ['late', 'now', 'soon'].includes(it.order.status) && it.order.packs > 0 && (
          <div className={`text-[10px] font-semibold ${it.order.status === 'soon' ? 'text-amber-700' : 'text-red-600'}`}>
            {it.order.pack_size > 1 ? `${formatNumber(it.order.packs)}× ` : `${formatNumber(it.order.need)} `}{t('bestellen')}
            {it.order.order_by && it.order.status !== 'late' && ` · ${fmtDate(it.order.order_by)}`}
          </div>
        )}
      </div>
      {/* actions — icon-only on phones */}
      {it.active ? (
        <div className="flex gap-1 shrink-0 sm:order-6 sm:ml-auto">
          <button onClick={() => setOpen((v) => !v)} title={t('Verbruik per weekdag, per week en uitschieters')}
            aria-label={t('Verloop')} aria-expanded={open}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-semibold ${open ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            <BarChart3 className="h-3.5 w-3.5" /><span className="hidden md:inline">{t('Verloop')}</span>
          </button>
          <button onClick={() => onQuick(it.id, 'count')} title={t('Telling')} aria-label={t('Telling')}
            className="inline-flex items-center gap-1 rounded-md bg-[#114817]/5 text-[#114817] px-2 py-1.5 text-[11px] font-semibold hover:bg-[#114817]/10">
            <ClipboardList className="h-3.5 w-3.5" /><span className="hidden md:inline">{t('Telling')}</span>
          </button>
          <button onClick={() => onQuick(it.id, 'delivery')} title={t('Levering')} aria-label={t('Levering')}
            className="inline-flex items-center gap-1 rounded-md bg-[#cf9a40]/10 text-[#7a5a1e] px-2 py-1.5 text-[11px] font-semibold hover:bg-[#cf9a40]/20">
            <Truck className="h-3.5 w-3.5" /><span className="hidden md:inline">{t('Levering')}</span>
          </button>
        </div>
      ) : <div className="sm:order-6 sm:ml-auto" />}
      {/* sparkline — full width under the name on mobile, a column on desktop */}
      <div className="basis-full sm:basis-auto sm:w-40 sm:order-2">
        <Sparkline history={it.history} t={t} unit={unit} />
      </div>
      {/* forecast + last count */}
      <div className="text-[10px] text-slate-500 tabular-nums basis-full sm:basis-auto sm:w-28 sm:order-4 flex sm:flex-col gap-x-3">
        <span>{t('week')} <b className="text-slate-700">{formatNumber(it.usage?.per_week ?? it.forecast_7)}</b></span>
        <span>{t('dag')} <b className="text-slate-700">{formatNumber(it.usage?.per_open_day ?? 0)}</b></span>
        <span className="hidden sm:inline text-slate-400">30 d {formatNumber(it.forecast_30)}</span>
        {it.last_count && <span className="lg:hidden text-slate-400">· {t('telling')} {fmtDate(it.last_count.date)}</span>}
      </div>
      <div className="hidden lg:block flex-1 min-w-0 text-[10px] text-slate-500 sm:order-5 truncate">
        {it.last_count
          ? <>{fmtDate(it.last_count.date)}: {formatNumber(it.last_count.qty)}{unit && ` ${unit}`} · +{formatNumber(it.deliveries_since)} {t('geleverd')} · −{formatNumber(it.used_since)} {t('verbruikt')}</>
          : <span className="text-amber-600 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {t('Nog geen telling — voer een eerste voorraadtelling in.')}</span>}
      </div>
    </div>
    {open && <UsagePanel it={it} t={t} fmtDate={fmtDate} />}
    </div>
  );
}

/* ------------------------------------------------------------ stock update */
function StockUpdate({ items, events, quick, t, fmtDate, reload }) {
  const [form, setForm] = useState({ packaging_id: '', kind: 'count', qty: '', date: today(), note: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const unitOf = (id) => { const u = items.find((i) => String(i.id) === String(id))?.unit; return u && u !== 'stuks' ? u : ''; };

  useEffect(() => {
    if (quick) setForm((f) => ({ ...f, packaging_id: quick.packaging_id, kind: quick.kind }));
  }, [quick]);

  const save = async () => {
    if (!form.packaging_id || form.qty === '') return;
    setBusy(true); setErr(null); setSaved(false);
    try {
      await api.addPackagingEvent(form);
      setForm((f) => ({ ...f, qty: '', note: '' }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || t('Opslaan mislukt.')); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(t('Deze beweging wissen?'))) return;
    setBusy(true); setErr(null);
    try { await api.addPackagingEvent({ delete_id: id }); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || t('Wissen mislukt.')); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard title={t('Voorraad bijwerken')}
      subtitle={t('Telling = absolute stand op het einde van die dag. Levering/correctie = verschil (+/−).')}>
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-end gap-2 mb-3">
        <select value={form.packaging_id} onChange={(e) => setForm({ ...form, packaging_id: e.target.value })}
          className={`${INPUT} col-span-2`}>
          <option value="">{t('— kies item —')}</option>
          {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={INPUT}>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
        </select>
        <input type="number" inputMode="decimal" placeholder={`${t('Aantal')}${unitOf(form.packaging_id) ? ` (${unitOf(form.packaging_id)})` : ''}`} value={form.qty}
          onChange={(e) => setForm({ ...form, qty: e.target.value })} className={`${INPUT} sm:w-28`} />
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={INPUT} />
        <input placeholder={t('Notitie (optioneel)')} value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className={`${INPUT} sm:flex-1 sm:min-w-[120px]`} />
        <button onClick={save} disabled={busy || !form.packaging_id || form.qty === ''}
          className="col-span-2 sm:col-span-1 rounded-lg bg-[#114817] text-white px-4 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold disabled:opacity-40">
          {t('Opslaan')}
        </button>
      </div>
      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
      {saved && <p className="text-xs text-[#114817] mb-2">✓ {t('Opgeslagen')}</p>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left border-b border-slate-100">
              <th className="py-1.5 font-medium">{t('Datum')}</th>
              <th className="font-medium">{t('Item')}</th>
              <th className="font-medium">{t('Type')}</th>
              <th className="font-medium text-right">{t('Aantal')}</th>
              <th className="font-medium hidden sm:table-cell">{t('Notitie')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => {
              const KindIcon = KIND_ICON[e.kind] || SlidersHorizontal;
              return (
                <tr key={e.id} className="border-b border-slate-50">
                  <td className="py-1.5 text-slate-500 text-xs whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="text-slate-700 text-xs">{e.packaging}</td>
                  <td className="text-xs">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${e.kind === 'count' ? 'bg-[#8bd4be]/30 text-[#114817]' : e.kind === 'delivery' ? 'bg-[#cf9a40]/15 text-[#7a5a1e]' : 'bg-slate-100 text-slate-500'}`}>
                      <KindIcon className="h-3 w-3" />{t(KIND_LABEL[e.kind] || e.kind)}
                    </span>
                  </td>
                  <td className="text-right tabular-nums text-slate-700 text-xs">{formatNumber(e.qty)}{unitOf(e.packaging_id) ? ` ${unitOf(e.packaging_id)}` : ''}</td>
                  <td className="text-xs text-slate-400 hidden sm:table-cell">{e.note}</td>
                  <td className="text-right">
                    <button title={t('Wissen')} aria-label={t('Wissen')} disabled={busy} onClick={() => remove(e.id)}
                      className="p-2 sm:p-1.5 text-slate-300 hover:text-red-500 disabled:opacity-40"><X className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!events.length && <p className="text-sm text-slate-400 py-2">{t('Nog geen bewegingen — start met een eerste telling.')}</p>}
      </div>
    </SectionCard>
  );
}

/* ------------------------------------------------------------ manage items */
function ManageItems({ items, t, reload }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async (body) => {
    setBusy(true); setErr(null);
    try { await api.setPackagingItem(body); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || t('Opslaan mislukt.')); }
    finally { setBusy(false); }
  };
  if (!items.length) return null;
  return (
    <SectionCard title={t('Items beheren')}
      subtitle={t('Leverancier, doosgrootte, levertijd en gewenste voorraad — samen bepalen ze wat er op de bestellijst komt.')}>
      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
      <div className="hidden sm:flex items-center gap-2 px-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400">
        <span className="flex-1">{t('Item')}</span>
        <span className="w-32">{t('Leverancier')}</span>
        <span className="w-20 text-right">{t('Per doos')}</span>
        <span className="w-20 text-right">{t('Levertijd')}</span>
        <span className="w-20 text-right">{t('Voorraad voor')}</span>
        <span className="w-16" />
      </div>
      <div className="space-y-1">
        {items.map((i) => <ManageRow key={i.id} i={i} t={t} busy={busy} save={save} />)}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        {t('Levertijd = dagen tussen bestellen en leveren. Voorraad voor = hoeveel dagen verbruik je in huis wil houden ná levering.')}
      </p>
    </SectionCard>
  );
}

/** One item's ordering parameters; each field saves when it loses focus. */
function ManageRow({ i, t, busy, save }) {
  const [f, setF] = useState({ supplier: i.order?.supplier || '', pack_size: i.order?.pack_size ?? 1,
    lead_days: i.order?.lead_days ?? 7, cover_days: i.order?.cover_days ?? 21 });
  const base = { id: i.id, name: i.name, kind: i.kind, active: i.active };
  const commit = (field) => {
    const v = f[field];
    const was = field === 'supplier' ? (i.order?.supplier || '') : i.order?.[field];
    if (String(v) !== String(was)) save({ ...base, [field]: v });
  };
  const num = (field, min) => (
    <input inputMode="numeric" value={f[field]} disabled={busy}
      onChange={(e) => setF({ ...f, [field]: e.target.value.replace(/[^0-9]/g, '') })}
      onBlur={() => { const n = Math.max(min, parseInt(f[field], 10) || min); setF({ ...f, [field]: n }); commit(field); }}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      className={`${INPUT} w-20 py-1 text-right tabular-nums`} />
  );
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 text-xs ${i.active ? 'bg-slate-50' : 'bg-slate-50/50 text-slate-400'}`}>
      <span className="min-w-0 flex-1 text-slate-700 truncate" title={i.name}>
        {i.name}<span className="ml-1.5 text-[10px] text-slate-400">{[i.kind, i.unit].filter(Boolean).join(' · ')}</span>
      </span>
      <input value={f.supplier} disabled={busy} placeholder={t('leverancier')}
        onChange={(e) => setF({ ...f, supplier: e.target.value })} onBlur={() => commit('supplier')}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} className={`${INPUT} w-32 py-1`} />
      {num('pack_size', 1)}
      {num('lead_days', 0)}
      {num('cover_days', 0)}
      <button disabled={busy} onClick={() => save({ ...base, active: !i.active })}
        className={`w-16 px-2.5 py-1 rounded text-[10px] font-semibold disabled:opacity-40 shrink-0 ${i.active ? 'bg-[#8bd4be]/30 text-[#114817]' : 'bg-slate-200 text-slate-500'}`}>
        {i.active ? t('actief') : t('inactief')}
      </button>
    </div>
  );
}

/* -------------------------------------------------- mapping: packaging (cup) */
const isDrink = (p) => /drink|drank|boisson/i.test(p.category || '');
const isYaourt = (p) => /yaourt|yoghurt|yogurt|granola/i.test(p.name || '');

// product_id -> links [{packaging_id, per_unit}] from the items' product lists
const useLinks = (items) => useMemo(() => {
  const m = {};
  items.forEach((it) => (it.products || []).forEach((p) => {
    (m[p.product_id] = m[p.product_id] || []).push({ packaging_id: it.id, per_unit: p.per_unit, name: it.name, unit: it.unit });
  }));
  return m;
}, [items]);

function MappingSection({ items, products, t, reload }) {
  const [tab, setTab] = useState('boissons');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const mapByProduct = useLinks(items);

  const counts = useMemo(() => ({
    boissons: products.filter(isDrink).length,
    yaourt: products.filter(isYaourt).length,
  }), [products]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return products.filter((p) => {
      if (tab === 'boissons' && !isDrink(p)) return false;
      if (tab === 'yaourt' && !isYaourt(p)) return false;
      if (ql && !p.name.toLowerCase().includes(ql)) return false;
      return true;
    }).slice(0, 300);
  }, [products, tab, q]);

  const setCup = async (p, newId) => {
    const cur = mapByProduct[p.product_id] || [];
    setBusy(true); setErr(null);
    try {
      if (newId) {
        await api.setPackagingMap({ product_id: p.product_id, packaging_id: Number(newId),
          qty_per_unit: cur[0]?.per_unit || 1 });
      }
      for (const m of cur) {
        if (String(m.packaging_id) !== String(newId)) {
          await api.setPackagingMap({ product_id: p.product_id, packaging_id: m.packaging_id, clear: true });
        }
      }
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || t('Opslaan mislukt.')); }
    finally { setBusy(false); }
  };
  const setFraction = async (p, packagingId, v) => {
    if (!(v > 0)) return;
    setBusy(true); setErr(null);
    try { await api.setPackagingMap({ product_id: p.product_id, packaging_id: packagingId, qty_per_unit: v }); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || t('Opslaan mislukt.')); }
    finally { setBusy(false); }
  };

  if (!items.length) return null;
  return (
    <SectionCard title={t('Koppeling producten ↔ verpakking')}
      subtitle={t('Kies per product welke cup het verbruikt. Enkel take-away-verkopen tellen mee; de fractie per stuk staat normaal op 1.')}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="inline-flex items-center bg-slate-100 rounded-lg p-0.5 text-xs font-medium">
          {[['boissons', `${t('Dranken')} (${counts.boissons})`],
            ['yaourt', `Yaourt & Granola (${counts.yaourt})`],
            ['all', t('Alle')]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-md ${tab === k ? 'bg-white text-[#114817] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{l}</button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Zoek product…')}
          className={`${INPUT} basis-full sm:basis-auto sm:flex-1 sm:max-w-[220px]`} />
      </div>
      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}

      <div className="divide-y divide-slate-100 sm:divide-slate-50">
        {rows.map((p) => {
          const links = mapByProduct[p.product_id] || [];
          const first = links[0];
          return (
            <div key={p.product_id} className="flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-1.5 py-2 sm:py-1.5">
              {/* phone: name on its own line, then the cup select stretched with the fraction beside it */}
              <div className="basis-full sm:basis-auto sm:flex-1 min-w-0">
                <div className="text-sm sm:text-xs text-slate-700 truncate">{p.name}</div>
                <div className="text-[10px] text-slate-400">
                  {formatNumber(p.units_120d)} {t('verkocht (120 d.)')}
                  {tab === 'all' && p.category ? ` · ${p.category}` : ''}
                </div>
              </div>
              <select value={first ? String(first.packaging_id) : ''} disabled={busy}
                onChange={(e) => setCup(p, e.target.value)}
                className={`${INPUT} flex-1 min-w-0 sm:flex-none sm:w-44 sm:order-3 ${first ? 'border-[#8bd4be] bg-[#8bd4be]/10' : ''}`}>
                <option value="">{t('— geen cup —')}</option>
                {items.map((i) => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
              </select>
              {first && (
                <label className="flex items-center gap-1 shrink-0 sm:order-2 text-[10px] text-slate-400">
                  <span className="sm:hidden">×</span>
                  <input key={`${p.product_id}-${first.per_unit}`} defaultValue={first.per_unit} inputMode="decimal"
                    title={t('per verkocht stuk')} aria-label={t('per verkocht stuk')}
                    onBlur={(e) => {
                      const v = parseFloat(e.target.value.replace(',', '.'));
                      if (v > 0 && v !== first.per_unit) setFraction(p, first.packaging_id, v);
                    }}
                    className={`${INPUT} w-16 text-right`} />
                  <span className="sm:hidden">{t('per stuk')}</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
      {!rows.length && <p className="text-sm text-slate-400 py-2">{t('Niets gevonden.')}</p>}
    </SectionCard>
  );
}

/* ------------------------------------------------- recipes: ingredients */
function RecipeSection({ items, products, t, reload }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState({});   // product_id -> {packaging_id, qty}
  const mapByProduct = useLinks(items);

  // without a search: the products that already have a recipe; with one: matches
  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return products.filter((p) => (ql ? p.name.toLowerCase().includes(ql) : (mapByProduct[p.product_id] || []).length > 0)).slice(0, 200);
  }, [products, q, mapByProduct]);

  const call = async (body) => {
    setBusy(true); setErr(null);
    try { await api.setPackagingMap(body); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || t('Opslaan mislukt.')); }
    finally { setBusy(false); }
  };
  const add = async (p) => {
    const d = draft[p.product_id] || {};
    const qty = parseFloat(String(d.qty || '').replace(',', '.'));
    if (!d.packaging_id || !(qty > 0)) return;
    await call({ product_id: p.product_id, packaging_id: Number(d.packaging_id), qty_per_unit: qty });
    setDraft((cur) => ({ ...cur, [p.product_id]: { packaging_id: '', qty: '' } }));
  };

  if (!items.length) return null;
  return (
    <SectionCard title={t('Recepten: ingrediënten per product')}
      subtitle={t('Per verkocht stuk — bv. 18 g koffiebonen en 150 ml melk voor een cappuccino. Alle verkopen tellen mee (ook eat-in).')}>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Zoek een product om een recept toe te voegen…')}
        className={`${INPUT} w-full sm:max-w-[320px] mb-3`} />
      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}

      <div className="divide-y divide-slate-50">
        {rows.map((p) => {
          const links = mapByProduct[p.product_id] || [];
          const d = draft[p.product_id] || { packaging_id: '', qty: '' };
          const free = items.filter((i) => !links.some((l) => l.packaging_id === i.id));
          return (
            <div key={p.product_id} className="py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm sm:text-xs text-slate-700 truncate">{p.name}</div>
                  <div className="text-[10px] text-slate-400">{formatNumber(p.units_120d)} {t('verkocht (120 d.)')}{p.category ? ` · ${p.category}` : ''}</div>
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {links.map((l) => (
                  <span key={l.packaging_id} className="inline-flex items-center gap-1 rounded-md bg-[#8bd4be]/15 border border-[#8bd4be]/60 pl-2 pr-1 py-0.5 text-xs text-[#114817]">
                    <input key={`${p.product_id}-${l.packaging_id}-${l.per_unit}`} defaultValue={l.per_unit} inputMode="decimal" title={t('per verkocht stuk')}
                      onBlur={(e) => { const v = parseFloat(e.target.value.replace(',', '.')); if (v > 0 && v !== l.per_unit) call({ product_id: p.product_id, packaging_id: l.packaging_id, qty_per_unit: v }); }}
                      className="w-14 bg-transparent text-right tabular-nums outline-none border-b border-transparent focus:border-[#114817]" />
                    <span className="text-[10px] text-slate-500">{l.unit && l.unit !== 'stuks' ? l.unit : '×'}</span>
                    <span className="font-medium">{l.name}</span>
                    <button disabled={busy} onClick={() => call({ product_id: p.product_id, packaging_id: l.packaging_id, clear: true })}
                      title={t('Wissen')} className="p-0.5 text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {free.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <select value={d.packaging_id} disabled={busy} onChange={(e) => setDraft((cur) => ({ ...cur, [p.product_id]: { ...d, packaging_id: e.target.value } }))}
                      className={`${INPUT} w-36 py-1`}>
                      <option value="">{t('+ ingrediënt')}</option>
                      {free.map((i) => <option key={i.id} value={String(i.id)}>{i.name}{i.unit && i.unit !== 'stuks' ? ` (${i.unit})` : ''}</option>)}
                    </select>
                    {d.packaging_id && (
                      <>
                        <input inputMode="decimal" placeholder={t('hoeveelheid')} value={d.qty} onKeyDown={(e) => e.key === 'Enter' && add(p)}
                          onChange={(e) => setDraft((cur) => ({ ...cur, [p.product_id]: { ...d, qty: e.target.value } }))}
                          className={`${INPUT} w-24 py-1 text-right`} />
                        <button disabled={busy} onClick={() => add(p)}
                          className="rounded-md bg-[#114817] text-white px-2.5 py-1.5 text-[11px] font-semibold disabled:opacity-40">{t('Toevoegen')}</button>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!rows.length && <p className="text-sm text-slate-400 py-2">{q ? t('Niets gevonden.') : t('Nog geen recepten — zoek een product hierboven.')}</p>}
    </SectionCard>
  );
}
